import { type FC, useState } from "react"

import type { WebsiteEditPreviewBlock } from "@forento/core/models/aiWebsite"
import type { LandingPageBlock } from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import BlockPreview, { type NavigationPreview } from "./blocks/preview/BlockPreview"

export function getAiPreviewBlockDomId(index: number): string {
	return `landing-editor-ai-preview-block-${index}`
}

export function getAiPreviewRemovedBlockDomId(position: number): string {
	return `landing-editor-ai-preview-removed-block-${position}`
}

const changeStyles = {
	created: { ring: "ring-emerald-500", badge: "bg-emerald-500", label: "New section" },
	updated: { ring: "ring-amber-500", badge: "bg-amber-500", label: "Rewritten" },
	removed: { ring: "ring-red-500", badge: "bg-red-500", label: "Removed" },
}

type Entry = {
	domId: string
	block: LandingPageBlock
	change: keyof typeof changeStyles | null
	previousBlock: LandingPageBlock | null
}

type Props = {
	blocks: WebsiteEditPreviewBlock[]
	removedBlocks: { index: number; block: LandingPageBlock }[]
	navigationPreview: NavigationPreview
}

const AiPreviewBlocks: FC<Props> = ({ blocks, removedBlocks, navigationPreview }) => {
	const toRemovedEntries = (isPosition: (index: number) => boolean): Entry[] =>
		removedBlocks.flatMap((removed, position) =>
			isPosition(removed.index)
				? [
						{
							domId: getAiPreviewRemovedBlockDomId(position),
							block: removed.block,
							change: "removed" as const,
							previousBlock: null,
						},
					]
				: [],
		)

	const entries: Entry[] = []
	blocks.forEach((entry, index) => {
		entries.push(...toRemovedEntries(removedIndex => removedIndex === index))
		entries.push({ domId: getAiPreviewBlockDomId(index), ...entry })
	})
	entries.push(...toRemovedEntries(removedIndex => removedIndex >= blocks.length))

	return (
		<>
			{entries.map((entry, index) => (
				<PreviewSection key={entry.domId} entry={entry} index={index} navigationPreview={navigationPreview} />
			))}
		</>
	)
}

type SectionProps = { entry: Entry; index: number; navigationPreview: NavigationPreview }

const PreviewSection: FC<SectionProps> = ({ entry, index, navigationPreview }) => {
	const [isShowingBefore, setShowingBefore] = useState(false)

	const style = entry.change !== null ? changeStyles[entry.change] : null
	const block = isShowingBefore && entry.previousBlock !== null ? entry.previousBlock : entry.block

	return (
		<div id={entry.domId} className={cn("relative", style !== null && "ring-3 ring-inset", style?.ring)}>
			<div className={cn(entry.change === "removed" && "opacity-45 grayscale")}>
				<BlockPreview block={block} index={index} navigationPreview={navigationPreview} />
			</div>
			{style !== null && (
				<div className="absolute top-3 left-3 flex items-center gap-2">
					<span
						className={cn(
							"rounded-full px-2.5 py-1 text-xs font-semibold text-white shadow-sm",
							style.badge,
						)}
					>
						{style.label}
					</span>
					{entry.previousBlock !== null && (
						<div className="flex rounded-full bg-white/95 p-0.5 shadow-sm ring-1 ring-black/10">
							{[true, false].map(isBefore => (
								<button
									key={String(isBefore)}
									type="button"
									aria-pressed={isShowingBefore === isBefore}
									onClick={() => setShowingBefore(isBefore)}
									className={cn(
										"cursor-pointer rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors",
										isShowingBefore === isBefore
											? "bg-gray-900 text-white"
											: "text-gray-600 hover:text-gray-900",
									)}
								>
									{isBefore ? "Before" : "After"}
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default AiPreviewBlocks
